import { Fragment, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import {
  EscrowInvitationForm,
  type IEscrowInvitationForm,
  type IEscrowInvitationFormHandle,
} from '@/components/Escrow/Invitation';
import { CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useOnboardingStepSubmit, useStepSubmitHandler } from 'src/context/onboarding-step-submit';

import { useBeneficiaryInvitationContext, useSaveInvitation } from '../model';

import InvitationOrganizationPicker from './InvitationOrganizationPicker';

const InvitationFormCard = () => {
  const { escrowInvitation, mode, setMode } = useBeneficiaryInvitationContext();
  const formRef = useRef<IEscrowInvitationFormHandle>(null);
  const saveInvitation = useSaveInvitation();
  const { clearStepError } = useOnboardingStepSubmit();
  const [isSaving, setIsSaving] = useState(false);
  const [pickedCompanyName, setPickedCompanyName] = useState<string | null>(null);
  const isPicking = mode === 'pick';
  const [isPickerInvalid, setIsPickerInvalid] = useState(false);

  const defaultValues =
    escrowInvitation && pickedCompanyName === null
      ? {
          companyName: escrowInvitation.companyName,
          userName: escrowInvitation.userName ?? '',
          userEmail: escrowInvitation.userEmail ?? '',
        }
      : { companyName: pickedCompanyName ?? '', userName: '', userEmail: '' };

  const pickOrganization = (companyName: string) => {
    setPickedCompanyName(companyName);
    setMode('form');
  };

  const startNewOrganization = () => {
    flushSync(() => {
      setPickedCompanyName('');
      setMode('form');
    });
    formRef.current?.focusCompanyName();
  };

  const cancelEditing = () => setMode(escrowInvitation ? 'view' : 'pick');

  const saveSubmittedValues = async (values: IEscrowInvitationForm) => {
    if (await saveInvitation(values)) clearStepError();
  };

  const submitStep = async () => {
    if (isPicking) {
      setIsPickerInvalid(true);
      return 'Select a beneficiary to continue';
    }

    const values = await formRef.current?.submit();

    if (!values) return 'Fix the highlighted fields to continue.';

    return saveInvitation(values);
  };

  useStepSubmitHandler(submitStep);

  const saveFromCard = async () => {
    const values = await formRef.current?.submit();

    if (!values) return;

    setIsSaving(true);

    try {
      await saveSubmittedValues(values);
    } finally {
      setIsSaving(false);
    }
  };

  if (isPicking)
    return (
      <CardContent>
        <InvitationOrganizationPicker
          onPick={pickOrganization}
          onAddNew={startNewOrganization}
          error={isPickerInvalid ? 'Select a beneficiary.' : undefined}
        />
      </CardContent>
    );

  return (
    <Fragment>
      <CardContent>
        <EscrowInvitationForm ref={formRef} onSubmit={saveSubmittedValues} defaultValues={defaultValues} hideActions />
      </CardContent>
      <CardFooter className="bg-muted py-3 justify-end gap-2 rounded-b-xl">
        <Button variant="secondary" size="sm" onClick={cancelEditing}>
          Cancel
        </Button>
        <Button size="sm" onClick={saveFromCard} isLoading={isSaving}>
          Save
        </Button>
      </CardFooter>
    </Fragment>
  );
};

export default InvitationFormCard;
